import React, { Fragment } from 'react'
import { noop } from 'lodash-es'
import { Translation, Locale } from 'locale'
import { User, AuthorisedUser, ValidationIssue, Organisation } from 'api'
import { userStatusTranslationMap, userStatusTranslationReverseMap } from 'model'
import { Groupbox } from 'view/components/panels'
import { TextInputField, DialogRow, DialogField } from 'view/components/inputs'
import { RadioButtonStripField } from 'view/components/buttons'
import { OrganisationSelectboxField } from 'view/shared/entity'
import * as constants from 'view/styles/constants'
import { formatDateAndTime } from 'locale/Locale'

interface UserDetailsPanelProps {
  user: User
  isNewUser: boolean
  organisation: Organisation | null
  authorisedUser: AuthorisedUser
  validationIssues: ValidationIssue[]
  readOnly: boolean
  onUpdateUser: (user: Partial<User>) => unknown
}

export const UserDetailsPanel: React.FunctionComponent<UserDetailsPanelProps> = props => {
  const { user, isNewUser, organisation, authorisedUser, validationIssues, readOnly, onUpdateUser } = props

  const updateHandler = <K extends keyof User>(key: K) => {
    return (value: User[K]) => {
      const update = {
        [key]: value
      }
      onUpdateUser(update)
    }
  }

  const canTradeUpdateHandler = (value: Translation) => {
    updateHandler('canTrade')(value === Translation.TC_ACTIVE)
  }

  const statusUpdateHandler = (value: Translation.TC_ENABLED | Translation.TC_DISABLED | Translation.TC_DELETED) => {
    updateHandler('status')(userStatusTranslationReverseMap[value])
  }

  return (
    <Fragment>
      <Groupbox>
        <DialogRow verticalLayout={constants.VerticalLayout.Bottom}>
          <DialogField>
            {!isNewUser ? (
              <TextInputField
                label={Locale.translate(Translation.SC_ORGANISATION)}
                onUpdate={noop}
                readOnly={true}
                value={organisation?.legalName}
              />
            ) : (
              <OrganisationSelectboxField
                dataTestId={'UserDetailsPanel-OrganisationSelectboxField-Organisation'}
                label={Locale.translate(Translation.SC_ORGANISATION)}
                fieldName="organisationId"
                validationIssues={validationIssues}
                placeholder={Locale.translate(Translation.SC_ORGANISATION_PLACEHOLDER)}
                value={user.organisationId}
                readOnly={readOnly}
                onUpdate={updateHandler('organisationId')}
              />
            )}
          </DialogField>

          <DialogField flex={0}>
            <RadioButtonStripField
              dataTestId={'UserDetailsPanel-RadioButtonStripField-Trading'}
              label={Locale.translate(Translation.SC_TRADING)}
              fieldName="canTrade"
              validationIssues={validationIssues}
              options={[Translation.TC_ACTIVE, Translation.TC_SUSPENDED]}
              selectedOption={user.canTrade ? Translation.TC_ACTIVE : Translation.TC_SUSPENDED}
              buttonWidth={80}
              disabled={readOnly}
              onUpdate={canTradeUpdateHandler}
            />
          </DialogField>
        </DialogRow>
        <DialogRow horizontalLayout={constants.HorizontalLayout.Right}>
          <DialogField flex={1}>
            <TextInputField
              dataTestId={'UserDetailsPanel-TextInputField-OKTAStatus'}
              label={Locale.translate(Translation.SC_OKTA_STATUS)}
              fieldName="oktaUserStatus"
              validationIssues={validationIssues}
              value={user.oktaUserStatus}
              readOnly={true}
              onUpdate={noop}
            />
          </DialogField>
          <DialogField flex={0}>
            <RadioButtonStripField
              dataTestId={'UserDetailsPanel-RadioButtonStripField-UserStatus'}
              label={Locale.translate(Translation.SC_USER_STATUS)}
              fieldName="status"
              validationIssues={validationIssues}
              options={[Translation.TC_ENABLED, Translation.TC_DISABLED, Translation.TC_DELETED]}
              selectedOption={userStatusTranslationMap[user.status]}
              buttonWidth={80}
              disabled={readOnly || authorisedUser.id === user.id}
              onUpdate={statusUpdateHandler}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <TextInputField
              label={Locale.translate(Translation.SC_OKTA_LAST_LOGIN)}
              fieldName="lastLogin"
              validationIssues={validationIssues}
              value={formatDateAndTime(user.oktaLastLogin, authorisedUser.userPreference.dateFormat)}
              readOnly={true}
              onUpdate={noop}
            />
          </DialogField>
        </DialogRow>
      </Groupbox>
      <Groupbox>
        <DialogRow>
          <DialogField flex={2}>
            <TextInputField
              dataTestId={'UserDetailsPanel-TextInputField-FirstName'}
              label={Locale.translate(Translation.SC_FIRST_NAME)}
              fieldName="firstName"
              validationIssues={validationIssues}
              value={user.firstName}
              maxLength={constants.nameMaxFieldLength}
              readOnly={readOnly}
              onUpdate={updateHandler('firstName')}
            />
          </DialogField>
          <DialogField flex={3}>
            <TextInputField
              dataTestId={'UserDetailsPanel-TextInputField-LastName'}
              label={Locale.translate(Translation.SC_LAST_NAME)}
              fieldName="lastName"
              validationIssues={validationIssues}
              value={user.lastName}
              maxLength={constants.nameMaxFieldLength}
              readOnly={readOnly}
              onUpdate={updateHandler('lastName')}
            />
          </DialogField>
        </DialogRow>
        {isNewUser && (
          <DialogRow>
            <DialogField>
              <TextInputField
                dataTestId={'UserDetailsPanel-TextInputField-Email'}
                label={Locale.translate(Translation.SC_EMAIL)}
                fieldName="username"
                validationIssues={validationIssues}
                value={user.username}
                maxLength={constants.emailMaxFieldLength}
                trimInput={true}
                readOnly={readOnly}
                onUpdate={updateHandler('username')}
              />
            </DialogField>
          </DialogRow>
        )}
        <DialogRow>
          <DialogField>
            <TextInputField
              dataTestId={'UserDetailsPanel-TextInputField-ContactNumber'}
              label={Locale.translate(Translation.SC_CONTACT_NUMBER)}
              fieldName="phoneNumber"
              validationIssues={validationIssues}
              value={user.phoneNumber}
              maxLength={constants.defaultMaxTextFieldLength}
              readOnly={readOnly}
              onUpdate={updateHandler('phoneNumber')}
            />
          </DialogField>
          <DialogField>
            <TextInputField
              dataTestId={'UserDetailsPanel-TextInputField-AlgoName'}
              label={Locale.translate(Translation.SC_ALGO_NAME)}
              fieldName="algoName"
              validationIssues={validationIssues}
              value={user.algoName}
              maxLength={constants.defaultMaxTextFieldLength}
              readOnly={readOnly}
              onUpdate={updateHandler('algoName')}
            />
          </DialogField>
        </DialogRow>
      </Groupbox>
      <Groupbox>
        <DialogRow>
          <DialogField>
            <TextInputField
              dataTestId={'UserDetailsPanel-TextInputField-AccountUserName'}
              label={Locale.translate(Translation.SC_ACCOUNT_USER_NAME)}
              value={user.username}
              readOnly={true}
              onUpdate={noop}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <TextInputField
              dataTestId={'UserDetailsPanel-TextInputField-ShortId'}
              label={Locale.translate(Translation.SC_SHORT_ID)}
              fieldName="shortId"
              validationIssues={validationIssues}
              value={user.shortId}
              maxLength={constants.shortIdMaxFieldLength}
              readOnly={readOnly}
              onUpdate={updateHandler('shortId')}
            />
          </DialogField>
        </DialogRow>
      </Groupbox>
    </Fragment>
  )
}
